import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  FilePlus,
  Loader2,
  Pencil,
  Trash2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  BarChart3,
  Mail,
  Image as ImageIcon,
  Star,
  Eye,
  Share2,
  Link2,
  Copy as CopyIcon,
  X as XIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchAdminBlogPosts,
  deleteBlogPost,
  fetchBlogAgentStatus,
  runBlogAgentNow,
  fetchPendingBlogPosts,
  approveBlogPost,
  rejectBlogPost,
  fetchPaywallResults,
  fetchNudgeStatus,
  runNudgeBatch,
  fetchNudgeResults,
  fetchAdminComments,
  approveComment,
  rejectComment,
  generateBlogOgCard,
  backfillBlogOgCards,
  toggleFeatureComment,
  fetchAdminRatingsSummary,
  mintBlogPreviewLink,
  listBlogPreviewLinks,
  revokeBlogPreviewLink,
  API,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
}

const AGENT_TOPICS = [
  ["anger", "Anger"], ["inner-child", "Inner child"], ["forgiveness", "Forgiveness"], ["patterns", "Patterns"],
  ["journaling", "Journaling"], ["relationships", "Relationships"], ["shame", "Shame"],
];

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [agentBusy, setAgentBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const [rowBusy, setRowBusy] = useState({});
  const [paywall, setPaywall] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [socialPost, setSocialPost] = useState(null); // post whose social copy drawer is open
  const [shareLinkPost, setShareLinkPost] = useState(null); // post whose share-link modal is open
  const [nudgeResults, setNudgeResults] = useState(null);
  const [pendingComments, setPendingComments] = useState([]);
  const [approvedComments, setApprovedComments] = useState([]);
  const [ratingsSummary, setRatingsSummary] = useState([]);
  const [commentBusy, setCommentBusy] = useState({});
  const [ogBusy, setOgBusy] = useState({});
  const [backfillBusy, setBackfillBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [r, a, p, pw, n, nr, cm, cma, rs] = await Promise.all([
        fetchAdminBlogPosts(),
        fetchBlogAgentStatus().catch(() => null),
        fetchPendingBlogPosts().catch(() => ({ posts: [] })),
        fetchPaywallResults().catch(() => null),
        fetchNudgeStatus().catch(() => null),
        fetchNudgeResults().catch(() => null),
        fetchAdminComments("pending").catch(() => ({ comments: [] })),
        fetchAdminComments("approved").catch(() => ({ comments: [] })),
        fetchAdminRatingsSummary().catch(() => []),
      ]);
      setPosts(r.posts || []);
      setAgent(a);
      setPending(p.posts || []);
      setPaywall(pw);
      setNudge(n);
      setNudgeResults(nr);
      setPendingComments(cm.comments || []);
      setApprovedComments(cma.comments || []);
      setRatingsSummary(rs || []);
    } catch {
      toast.error("Could not load posts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const [agentTopic, setAgentTopic] = useState("");
  const runAgent = async () => {
    const label = agentTopic ? `an essay on ${agentTopic.replace("-", " ")}` : "a new post";
    if (!confirm(`Draft ${label}? Claude will write it and drop it in the review queue — nothing publishes until you approve.`)) return;
    try {
      setAgentBusy(true);
      const res = await runBlogAgentNow(agentTopic || undefined);
      if (res.generated) {
        toast.success(`Drafted: ${res.title} — review pending`);
        await load();
      } else {
        toast.error(`Agent did not draft (${res.reason || "unknown"})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Agent run failed");
    } finally {
      setAgentBusy(false);
    }
  };

  const approve = async (id, title) => {
    try {
      setRowBusy((m) => ({ ...m, [id]: true }));
      await approveBlogPost(id);
      toast.success(`Approved: ${title}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setRowBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const reject = async (id, title) => {
    if (!confirm(`Reject "${title}"? This soft-deletes the draft.`)) return;
    try {
      setRowBusy((m) => ({ ...m, [id]: true }));
      await rejectBlogPost(id);
      toast.success(`Rejected: ${title}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setRowBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const runNudge = async () => {
    if (!confirm("Send nudge emails to eligible subscribers now? This bypasses the hourly scheduler.")) return;
    try {
      setNudgeBusy(true);
      const res = await runNudgeBatch();
      toast.success(`Nudges: sent=${res.sent} · already_converted=${res.already_converted}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge run failed");
    } finally {
      setNudgeBusy(false);
    }
  };

  const approveCommentRow = async (id, author) => {
    try {
      setCommentBusy((m) => ({ ...m, [id]: true }));
      await approveComment(id);
      toast.success(`Approved reflection from ${author}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setCommentBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const rejectCommentRow = async (id, author) => {
    if (!confirm(`Reject reflection from ${author}? This soft-deletes it.`)) return;
    try {
      setCommentBusy((m) => ({ ...m, [id]: true }));
      await rejectComment(id);
      toast.success("Rejected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setCommentBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const remove = async (id, title) => {
    if (!confirm(`Delete "${title}"? This soft-deletes the post from public view.`)) return;
    try {
      await deleteBlogPost(id);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const genOg = async (id, title) => {
    try {
      setOgBusy((m) => ({ ...m, [id]: true }));
      await generateBlogOgCard(id);
      toast.success(`OG card ready for "${title}"`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OG card generation failed");
    } finally {
      setOgBusy((m) => ({ ...m, [id]: false }));
    }
  };

  const backfillOg = async () => {
    if (!confirm("Generate OG cards for every published post that doesn't have one? Runs sequentially and uses your Emergent LLM key.")) return;
    try {
      setBackfillBusy(true);
      const res = await backfillBlogOgCards();
      toast.success(`Backfill done — ${res.processed} post(s) processed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillBusy(false);
    }
  };

  const featureComment = async (id, author, currentlyFeatured) => {
    try {
      setCommentBusy((m) => ({ ...m, [id]: true }));
      const res = await toggleFeatureComment(id);
      toast.success(
        res.featured
          ? `Featuring ${author}'s reflection on the landing page`
          : `Removed ${author}'s reflection from the landing`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feature toggle failed");
    } finally {
      setCommentBusy((m) => ({ ...m, [id]: false }));
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2">
              SEO
            </p>
            <h1 className="font-serif text-4xl text-bone-100">Blog posts</h1>
            <p className="font-serif italic text-bone-400 mt-1">
              Long-form Markdown essays with meta descriptions, hero image, category, and tags.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-blog-view-public"
              className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta"
            >
              <ExternalLink className="h-3 w-3" /> View blog
            </Link>
            <button
              onClick={backfillOg}
              disabled={backfillBusy}
              data-testid="admin-blog-og-backfill"
              className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta disabled:opacity-40"
              title="Generate OG share cards for every published post that doesn't have one"
            >
              {backfillBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <ImageIcon className="h-3 w-3" />
              )}
              Backfill OG cards
            </button>
            <Link to="/admin/blog/new" data-testid="admin-blog-new-btn">
              <Button className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5">
                <FilePlus className="h-4 w-4 mr-2" /> New post
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-bone-400">
            <Loader2 className="h-4 w-4 animate-spin" /> loading
          </div>
        ) : (
          <>
            {agent && (
              <div
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8 flex flex-wrap items-center gap-4 justify-between"
                data-testid="admin-blog-agent-card"
              >
                <div className="min-w-0">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1">
                    Auto-author agent · Claude Sonnet 4.6
                  </p>
                  <p className="font-serif italic text-bone-300 text-sm">
                    {agent.hours_since_latest == null
                      ? "No posts yet — the agent will draft one on its next tick."
                      : `Last draft ${agent.hours_since_latest}h ago. ` +
                        (agent.will_generate_on_next_tick
                          ? "The agent will draft on the next hourly tick."
                          : `Next draft after ${agent.stale_after_hours}h of silence.`)}
                  </p>
                  <p className="font-sans text-[0.6rem] text-bone-500 mt-1">
                    Stale threshold: {agent.stale_after_hours}h · Scheduler: {agent.scheduler_running ? "running" : "stopped"}
                    {typeof agent.pending_review === "number" ? ` · Pending review: ${agent.pending_review}` : ""}
                  </p>
                </div>
                <label className="flex flex-col gap-1 font-sans text-[0.6rem] uppercase tracking-[0.2em] text-bone-500">
                  Draft an essay on…
                  <select
                    value={agentTopic}
                    onChange={(e) => setAgentTopic(e.target.value)}
                    data-testid="admin-blog-agent-topic"
                    className="bg-obsidian-900 border border-obsidian-600 text-bone-100 h-11 px-3 font-sans text-sm normal-case tracking-normal"
                  >
                    <option value="">Any fresh topic</option>
                    {AGENT_TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <Button
                  onClick={runAgent}
                  disabled={agentBusy}
                  data-testid="admin-blog-agent-run"
                  className="rounded-none bg-obsidian-900 border border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 font-sans uppercase tracking-wider text-xs px-6 py-4"
                >
                  {agentBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {agentTopic ? "Draft this topic" : "Force draft now"}
                </Button>
              </div>
            )}

            {pending.length > 0 && (
              <section
                className="border border-terracotta/40 bg-terracotta/5 p-5 mb-8"
                data-testid="admin-blog-pending"
                aria-label="AI drafts awaiting review"
              >
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1">
                      Review queue · {pending.length} draft{pending.length === 1 ? "" : "s"}
                    </p>
                    <p className="font-serif italic text-bone-400 text-sm">
                      AI-drafted essays never go live until you approve them.
                    </p>
                  </div>
                </div>
                <ul className="space-y-4 list-none p-0">
                  {pending.map((p) => (
                    <li key={p.id} className="border border-obsidian-600 bg-obsidian-900/50 p-4" data-testid={`admin-blog-pending-${p.id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="font-sans text-[0.55rem] uppercase tracking-[0.24em] text-bone-500 mb-1">
                            {p.category} · {p.reading_time} min · {(p.tags || []).slice(0, 3).join(", ")}
                          </p>
                          <h3 className="font-serif text-xl text-bone-100 leading-snug mb-1">{p.title}</h3>
                          <p className="font-serif italic text-bone-400 text-sm leading-relaxed line-clamp-3">
                            {p.excerpt || p.meta_description}
                          </p>
                          <p className="font-sans text-[0.6rem] text-bone-500 mt-2">
                            /{p.slug}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <Link
                            to={`/admin/blog/edit/${p.id}`}
                            data-testid={`admin-blog-pending-edit-${p.id}`}
                            className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta inline-flex items-center gap-2"
                          >
                            <Pencil className="h-3 w-3" /> Edit first
                          </Link>
                          <Button
                            onClick={() => approve(p.id, p.title)}
                            disabled={rowBusy[p.id]}
                            data-testid={`admin-blog-pending-approve-${p.id}`}
                            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-[0.65rem] px-4 py-2"
                          >
                            {rowBusy[p.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                            Approve
                          </Button>
                          <button
                            onClick={() => reject(p.id, p.title)}
                            disabled={rowBusy[p.id]}
                            data-testid={`admin-blog-pending-reject-${p.id}`}
                            className="border border-obsidian-600 text-bone-500 hover:text-red-400 hover:border-red-400 font-sans uppercase tracking-wider text-[0.65rem] px-4 py-2 inline-flex items-center gap-1"
                          >
                            <XCircle className="h-3 w-3" /> Reject
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {paywall && paywall.results && paywall.results.some((r) => r.views > 0) && (
              <section
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8"
                data-testid="admin-blog-paywall-card"
                aria-label="Paywall A/B results"
              >
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-3 inline-flex items-center gap-2">
                  <BarChart3 className="h-3 w-3" />
                  A/B · paywall length
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {paywall.results.map((r) => (
                    <div
                      key={r.variant}
                      className="border border-obsidian-600 p-3"
                      data-testid={`admin-blog-paywall-variant-${r.variant}`}
                    >
                      <p className="font-serif text-lg text-bone-100">{r.label}</p>
                      <p className="font-sans text-xs text-bone-500 mt-1">
                        {r.views} view{r.views === 1 ? "" : "s"} · {r.clicks} click{r.clicks === 1 ? "" : "s"}
                      </p>
                      <p className="font-sans uppercase tracking-[0.24em] text-terracotta text-xl mt-2">
                        {r.ctr_percent}% CTR
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {nudge && (
              <div
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8 flex flex-wrap items-center gap-4 justify-between"
                data-testid="admin-nudge-card"
              >
                <div className="min-w-0">
                  <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1 inline-flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    Newsletter · trial nudge
                  </p>
                  <p className="font-serif italic text-bone-300 text-sm">
                    {nudge.eligible_now === 0
                      ? "No subscribers are due for a nudge right now."
                      : `${nudge.eligible_now} subscriber${nudge.eligible_now === 1 ? "" : "s"} eligible for the 3-day nudge.`}
                  </p>
                  <p className="font-sans text-[0.6rem] text-bone-500 mt-1">
                    Fires {nudge.nudge_after_hours}h after signup · Already nudged: {nudge.already_nudged} · Scheduler: {nudge.scheduler_running ? "running" : "stopped"}
                  </p>
                </div>
                <Button
                  onClick={runNudge}
                  disabled={nudgeBusy || nudge.eligible_now === 0}
                  data-testid="admin-nudge-run"
                  className="rounded-none bg-obsidian-900 border border-terracotta text-terracotta hover:bg-terracotta hover:text-obsidian-900 disabled:opacity-40 font-sans uppercase tracking-wider text-xs px-6 py-4"
                >
                  {nudgeBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Send nudges now
                </Button>
              </div>
            )}

            {nudgeResults && nudgeResults.results && nudgeResults.results.some((r) => r.sent > 0) && (
              <section
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8"
                data-testid="admin-nudge-ab"
                aria-label="Nudge subject A/B results"
              >
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-3 inline-flex items-center gap-2">
                  <BarChart3 className="h-3 w-3" />
                  A/B · nudge subject line
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {nudgeResults.results.map((r) => (
                    <div
                      key={r.variant}
                      className="border border-obsidian-600 p-3"
                      data-testid={`admin-nudge-variant-${r.variant}`}
                    >
                      <p className="font-serif text-base text-bone-100 leading-snug">{r.subject}</p>
                      <p className="font-sans text-xs text-bone-500 mt-1">
                        {r.sent} sent · {r.unique_clicks} clicked ({r.click_events} events)
                      </p>
                      <p className="font-sans uppercase tracking-[0.24em] text-terracotta text-xl mt-2">
                        {r.ctr_percent}% CTR
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {ratingsSummary.length > 0 && (
              <section
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8"
                data-testid="admin-ratings-summary"
                aria-label="Per-essay rating summary"
              >
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1 inline-flex items-center gap-2">
                  <Star className="h-3 w-3 fill-terracotta" />
                  Per-essay reader ratings
                </p>
                <p className="font-serif italic text-bone-400 text-sm mb-4">
                  Which essays are landing. Sorted by review count. These averages drive the SEO star ratings under each essay in Google Search.
                </p>
                <table className="w-full font-sans text-sm">
                  <thead>
                    <tr className="border-b border-obsidian-600 text-[0.6rem] uppercase tracking-[0.24em] text-bone-500">
                      <th className="text-left py-2 pr-4">Essay</th>
                      <th className="text-right py-2 pr-4 whitespace-nowrap">Avg</th>
                      <th className="text-right py-2 whitespace-nowrap">Reviews</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratingsSummary.map((r) => (
                      <tr
                        key={r.post_slug}
                        className="border-b border-obsidian-800/60 last:border-b-0"
                        data-testid={`admin-rating-row-${r.post_slug}`}
                      >
                        <td className="py-2 pr-4 text-bone-200 truncate max-w-md">
                          <a
                            href={`/blog/${r.post_slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-terracotta"
                          >
                            {r.title || r.post_slug}
                          </a>
                        </td>
                        <td className="text-right py-2 pr-4">
                          <span className="inline-flex items-center gap-1 text-terracotta">
                            <Star className="h-3 w-3 fill-terracotta" />
                            <span className="text-bone-100 font-semibold">{r.avg.toFixed(2)}</span>
                          </span>
                        </td>
                        <td className="text-right py-2 text-bone-300">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {approvedComments.length > 0 && (
              <section
                className="border border-obsidian-600 bg-obsidian-800/30 p-5 mb-8"
                data-testid="admin-comments-approved"
                aria-label="Approved reflections — feature on landing"
              >
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-1 inline-flex items-center gap-2">
                  <Star className="h-3 w-3" />
                  Landing testimonial rotator · pick which reflections show
                </p>
                <p className="font-serif italic text-bone-400 text-sm mb-4">
                  Featured reflections show near the Sign in with Google CTA. If no comment is starred, the rotator falls back to your latest approved reflections.
                </p>
                <ul className="space-y-3 list-none p-0">
                  {approvedComments.slice(0, 15).map((c) => (
                    <li
                      key={c.id}
                      className="border border-obsidian-600 bg-obsidian-900/50 p-4"
                      data-testid={`admin-comment-approved-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 mb-1">
                            <span className="text-bone-300">{c.author_name}</span>
                            <span> · /{c.post_slug}</span>
                            {c.featured && (
                              <span className="text-terracotta ml-2">· featured</span>
                            )}
                          </p>
                          <p className="font-serif text-bone-200 text-[0.95rem] leading-relaxed whitespace-pre-wrap line-clamp-3">
                            {c.body}
                          </p>
                        </div>
                        <button
                          onClick={() => featureComment(c.id, c.author_name, c.featured)}
                          disabled={commentBusy[c.id]}
                          data-testid={`admin-comment-feature-${c.id}`}
                          className={`inline-flex items-center gap-1 font-sans uppercase tracking-wider text-[0.65rem] px-3 py-2 border ${
                            c.featured
                              ? "border-terracotta text-terracotta bg-terracotta/10"
                              : "border-obsidian-600 text-bone-400 hover:border-terracotta hover:text-terracotta"
                          }`}
                          title={c.featured ? "Remove from landing rotator" : "Feature on the landing page"}
                        >
                          {commentBusy[c.id] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Star className={`h-3 w-3 ${c.featured ? "fill-terracotta" : ""}`} />
                          )}
                          {c.featured ? "Featured" : "Feature"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {pendingComments.length > 0 && (
              <section
                className="border border-terracotta/40 bg-terracotta/5 p-5 mb-8"
                data-testid="admin-comments-pending"
                aria-label="Comments awaiting moderation"
              >
                <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-3 inline-flex items-center gap-2">
                  Reflections · {pendingComments.length} awaiting review
                </p>
                <ul className="space-y-3 list-none p-0">
                  {pendingComments.map((c) => (
                    <li
                      key={c.id}
                      className="border border-obsidian-600 bg-obsidian-900/50 p-4"
                      data-testid={`admin-comment-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 mb-1">
                            <span className="text-bone-300">{c.author_name}</span>
                            <span> · {c.author_email}</span>
                            <span> · /{c.post_slug}</span>
                          </p>
                          <p className="font-serif text-bone-200 text-[0.95rem] leading-relaxed whitespace-pre-wrap">
                            {c.body}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          <Button
                            onClick={() => approveCommentRow(c.id, c.author_name)}
                            disabled={commentBusy[c.id]}
                            data-testid={`admin-comment-approve-${c.id}`}
                            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-obsidian-900 font-sans uppercase tracking-wider text-[0.65rem] px-4 py-2"
                          >
                            {commentBusy[c.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                            Approve
                          </Button>
                          <button
                            onClick={() => rejectCommentRow(c.id, c.author_name)}
                            disabled={commentBusy[c.id]}
                            data-testid={`admin-comment-reject-${c.id}`}
                            className="border border-obsidian-600 text-bone-500 hover:text-red-400 hover:border-red-400 font-sans uppercase tracking-wider text-[0.65rem] px-4 py-2 inline-flex items-center gap-1"
                          >
                            <XCircle className="h-3 w-3" /> Reject
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {posts.length === 0 ? (
              <p className="font-sans text-bone-500 text-sm p-6 border border-obsidian-600">
                No posts yet — publish your first entry to seed the sitemap.
              </p>
            ) : (
              <div className="border border-obsidian-600" data-testid="admin-blog-list">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-bone-500 border-b border-obsidian-600 bg-obsidian-800/40">
              <div className="col-span-5">Title</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Published</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {posts.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-blog-row-${p.id}`}
                className="grid grid-cols-12 gap-4 px-5 py-4 items-center border-b border-obsidian-600 last:border-b-0 hover:bg-obsidian-800/30 transition-colors"
              >
                <div className="col-span-5 min-w-0">
                  <p className="font-serif text-lg text-bone-100 truncate">{p.title}</p>
                  <p className="font-sans text-xs text-bone-500 truncate">/{p.slug}</p>
                </div>
                <div className="col-span-2 font-sans text-xs uppercase tracking-wider text-bone-400">
                  {p.category || "—"}
                </div>
                <div className="col-span-2 font-sans text-xs text-bone-400">
                  {formatDate(p.published_at)}
                </div>
                <div className="col-span-2">
                  {!p.published ? (
                    <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-bone-500 border border-obsidian-600 px-2 py-0.5">
                      Draft
                    </span>
                  ) : p.published_at && p.published_at > new Date().toISOString() ? (
                    <span
                      className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 px-2 py-0.5"
                      data-testid={`admin-blog-scheduled-${p.id}`}
                      title={`Publishes ${formatDate(p.published_at)}`}
                    >
                      Scheduled
                    </span>
                  ) : (
                    <span className="font-sans text-[0.55rem] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-400/40 px-2 py-0.5">
                      Live
                    </span>
                  )}
                </div>
                <div className="col-span-1 flex items-center gap-2 justify-end">
                  {p.social_copy && Object.keys(p.social_copy).length > 0 && (
                    <button
                      onClick={() => setSocialPost(p)}
                      data-testid={`admin-blog-social-${p.id}`}
                      className="text-bone-500 hover:text-terracotta p-1"
                      title="View social preview copy"
                    >
                      <Share2 className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setShareLinkPost(p)}
                    data-testid={`admin-blog-share-link-${p.id}`}
                    className="text-bone-500 hover:text-terracotta p-1"
                    title="Create a time-limited share-preview link"
                  >
                    <Link2 className="h-4 w-4" />
                  </button>
                  {p.published_at && p.published_at > new Date().toISOString() && (
                    <a
                      href={`/blog/preview/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-blog-preview-${p.id}`}
                      className="text-bone-500 hover:text-terracotta p-1"
                      title="Preview before go-live"
                    >
                      <Eye className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() => genOg(p.id, p.title)}
                    disabled={ogBusy[p.id]}
                    data-testid={`admin-blog-og-${p.id}`}
                    className={`text-bone-500 hover:text-terracotta p-1 ${p.og_image_url ? "text-terracotta" : ""}`}
                    title={p.og_image_url ? "Regenerate OG share card" : "Generate OG share card"}
                  >
                    {ogBusy[p.id] ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImageIcon className="h-4 w-4" />
                    )}
                  </button>
                  <Link
                    to={`/admin/blog/edit/${p.id}`}
                    data-testid={`admin-blog-edit-${p.id}`}
                    className="text-bone-500 hover:text-terracotta p-1"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => remove(p.id, p.title)}
                    data-testid={`admin-blog-delete-${p.id}`}
                    className="text-bone-500 hover:text-red-400 p-1"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
            )}
          </>
        )}
      </div>

      {/* Social copy drawer — opens when admin clicks the share icon */}
      {socialPost && (
        <SocialCopyDrawer post={socialPost} onClose={() => setSocialPost(null)} />
      )}
      {/* Share-link modal — opens when admin clicks the link icon */}
      {shareLinkPost && (
        <ShareLinkModal post={shareLinkPost} onClose={() => setShareLinkPost(null)} />
      )}
    </div>
  );
}

function SocialCopyDrawer({ post, onClose }) {
  const copies = post.social_copy || {};
  const clicks = post.social_clicks || {};
  const copyChannel = async (channel, text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${channel[0].toUpperCase()}${channel.slice(1)} copy copied`);
    } catch {
      toast.error("Clipboard blocked — try copying manually");
    }
  };
  const copyLink = async (variant) => {
    const link = `${API}/blog/track/${post.id}/${variant}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`Tracked ${variant.toUpperCase()} link copied`);
    } catch {
      toast.error("Clipboard blocked — try copying manually");
    }
  };
  // Build the ordered channel list. Keep A/B rendered together.
  const channels = Object.entries(copies);
  const releaseStr = post.published_at
    ? new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-obsidian-900/70 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-social-drawer"
    >
      <div
        className="w-full max-w-2xl bg-obsidian-900 border border-terracotta/60 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-obsidian-600">
          <div className="min-w-0">
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
              Social preview · {releaseStr || "scheduled"}
            </p>
            <h2 className="font-serif text-xl text-bone-100 truncate">{post.title}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-social-drawer-close"
            className="text-bone-400 hover:text-terracotta p-2"
            aria-label="Close"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {channels.length === 0 ? (
            <p className="font-serif italic text-bone-400">No social copy drafted for this post.</p>
          ) : (
            channels.map(([channel, text]) => {
              const variantKey = channel === "twitter_a" ? "a"
                : channel === "twitter_b" ? "b"
                : channel === "linkedin" ? "linkedin"
                : channel === "instagram" ? "instagram"
                : null;
              const clickCount = variantKey ? (clicks[variantKey] || 0) : null;
              return (
                <div
                  key={channel}
                  data-testid={`admin-social-${channel}`}
                  className="border border-obsidian-600 p-4"
                >
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-terracotta">
                      {channel.replace("_", " · ")}
                      {clickCount !== null && (
                        <span
                          className="normal-case tracking-normal ml-2 text-bone-400"
                          data-testid={`admin-social-clicks-${channel}`}
                        >
                          · {clickCount} click{clickCount === 1 ? "" : "s"}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-2">
                      {variantKey && (
                        <button
                          onClick={() => copyLink(variantKey)}
                          data-testid={`admin-social-copy-link-${channel}`}
                          className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-300 hover:text-terracotta px-2 py-1 min-h-[32px]"
                          title="Copy the tracked redirect URL (counts clicks per variant)"
                        >
                          <Link2 className="h-3 w-3" /> Link
                        </button>
                      )}
                      <button
                        onClick={() => copyChannel(channel, text)}
                        data-testid={`admin-social-copy-${channel}`}
                        className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-300 hover:text-terracotta px-2 py-1 min-h-[32px]"
                      >
                        <CopyIcon className="h-3 w-3" /> Copy
                      </button>
                    </div>
                  </div>
                  <p className="font-serif text-bone-200 leading-relaxed whitespace-pre-wrap text-sm">{text}</p>
                </div>
              );
            })
          )}
          {channels.some(([k]) => k === "twitter_a" || k === "twitter_b") && (
            <p className="font-serif italic text-bone-500 text-xs">
              Paste the tracked link above into each Twitter variant. Click-throughs for A vs B
              will show above after launch day.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareLinkModal({ post, onClose }) {
  const [ttl, setTtl] = useState(72);
  const [label, setLabel] = useState("");
  const [minting, setMinting] = useState(false);
  const [latestUrl, setLatestUrl] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const r = await listBlogPreviewLinks(post.id);
      setLinks(r.links || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [post.id]);

  const mint = async () => {
    if (minting) return;
    setMinting(true);
    try {
      const r = await mintBlogPreviewLink(post.id, { ttl_hours: Number(ttl) || 72, label });
      setLatestUrl(r.url);
      setLabel("");
      await load();
      toast.success("Share link ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create link");
    } finally {
      setMinting(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Clipboard blocked");
    }
  };

  const revoke = async (link_id) => {
    try {
      await revokeBlogPreviewLink(link_id);
      await load();
      toast.success("Link revoked");
    } catch {
      toast.error("Revoke failed");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-obsidian-900/70 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-share-link-modal"
    >
      <div
        className="w-full max-w-xl bg-obsidian-900 border border-terracotta/60 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-obsidian-600">
          <div className="min-w-0">
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-1">
              Shareable preview link
            </p>
            <h2 className="font-serif text-xl text-bone-100 truncate">{post.title}</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-share-link-close"
            className="text-bone-400 hover:text-terracotta p-2"
            aria-label="Close"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-5">
          <p className="font-serif italic text-bone-400 text-sm">
            Mint a token-signed URL for proofreaders. No admin login required to open — the
            link expires on its own.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="col-span-1">
              <span className="block font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-400 mb-1">
                Expires (hours)
              </span>
              <input
                type="number"
                value={ttl}
                min={1}
                max={720}
                onChange={(e) => setTtl(e.target.value)}
                data-testid="admin-share-link-ttl"
                className="w-full bg-obsidian-800 border border-obsidian-600 px-3 py-2 text-bone-100 font-serif focus:outline-none focus:border-terracotta"
              />
            </label>
            <label className="col-span-2">
              <span className="block font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-400 mb-1">
                Label (optional)
              </span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. editor, proofreader"
                data-testid="admin-share-link-label"
                className="w-full bg-obsidian-800 border border-obsidian-600 px-3 py-2 text-bone-100 font-serif focus:outline-none focus:border-terracotta"
              />
            </label>
          </div>
          <button
            onClick={mint}
            disabled={minting}
            data-testid="admin-share-link-mint"
            className="inline-flex items-center gap-2 bg-terracotta/90 hover:bg-terracotta text-obsidian-900 font-sans uppercase tracking-[0.18em] text-xs px-5 py-2.5 min-h-[44px] disabled:opacity-50"
          >
            {minting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Mint new link
          </button>

          {latestUrl && (
            <div className="border border-terracotta/60 bg-terracotta/5 p-4" data-testid="admin-share-link-latest">
              <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-terracotta mb-2">
                Just now · copy once (never shown again)
              </p>
              <p className="font-mono text-xs text-bone-100 break-all mb-2">{latestUrl}</p>
              <button
                onClick={() => copy(latestUrl)}
                data-testid="admin-share-link-copy"
                className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-200 hover:text-terracotta"
              >
                <CopyIcon className="h-3 w-3" /> Copy URL
              </button>
            </div>
          )}

          <div>
            <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-400 mb-2">
              Active links
            </p>
            {loading ? (
              <p className="font-serif italic text-bone-500 text-sm">Loading…</p>
            ) : links.length === 0 ? (
              <p className="font-serif italic text-bone-500 text-sm">No active links.</p>
            ) : (
              <ul className="space-y-2">
                {links.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-2 border border-obsidian-700 px-3 py-2"
                    data-testid={`admin-share-link-row-${l.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-sans text-xs text-bone-200 truncate">
                        {l.label || "— unlabeled —"}
                        {l.expired && <span className="text-red-400 ml-2">expired</span>}
                      </p>
                      <p className="font-sans text-[0.65rem] text-bone-500">
                        {l.views || 0} view{(l.views || 0) === 1 ? "" : "s"} · expires {new Date(l.expires_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => revoke(l.id)}
                      data-testid={`admin-share-link-revoke-${l.id}`}
                      className="text-bone-500 hover:text-red-400 p-1"
                      title="Revoke this link"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
